import {
  Autocomplete,
  TextField,
  Box,
  Button,
  Skeleton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePartyManager } from './PartyManagerContext';
import {
  POSSIBLE_SOURCES,
  EDIT_SOURCE,
  CODEMETA_SOURCE,
} from './partyManagerUtils';
import { useRouteLoaderData } from 'react-router-dom';

const ResponsibleUserSelector = ({ label }) => {
  const { users } = useRouteLoaderData('auth');

  const {
    partyData,
    isUIBlocked,
    handleFieldChange,
    handleCycleSource,
    getAvailableSources,
    isPartyManagerLoading,
    responsibleUserWarnings,
    getCustomOptionLabel,
    renderCustomOption,
  } = usePartyManager();

  const fieldKey = 'responsibleUserId';
  const fieldState = partyData[fieldKey];

  if (!fieldState) {
    return null;
  }

  const availableSources = getAvailableSources(fieldKey);
  const selectedId =
    fieldState.selected === 'edit'
      ? fieldState.edit
      : fieldState[fieldState.selected];

  const selectedOption = users.find((u) => u._id === selectedId) || null;

  const warnings = Object.values(responsibleUserWarnings).flat();
  const warningTitle =
    warnings.length > 0 ? (
      <div>
        <p>Folgende Probleme wurden festgestellt:</p>
        <ul>
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      </div>
    ) : (
      ''
    );

  const getButtonIcon = () => {
    const selectedSource = fieldState.selected;
    if (selectedSource === 'edit') return EDIT_SOURCE.icon;
    if (selectedSource === 'codemeta') {
      return (
        <img
          src={CODEMETA_SOURCE.icon}
          alt={CODEMETA_SOURCE.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    const sourceInfo = POSSIBLE_SOURCES[selectedSource];
    if (sourceInfo?.icon) {
      return (
        <img
          src={sourceInfo.icon}
          alt={sourceInfo.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    return '?';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
      }}
    >
      {isPartyManagerLoading ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <>
          <Autocomplete
            id="responsible-user-select"
            fullWidth
            options={users.filter((u) => u?.weclappId)}
            size="small"
            getOptionLabel={getCustomOptionLabel}
            renderOption={renderCustomOption}
            value={selectedOption}
            onChange={(event, newValue) => {
              handleFieldChange(fieldKey, newValue ? newValue._id : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                sx={{
                  '& .MuiInputBase-input': {
                    color: 'rgba(0, 0, 0, 0.87)',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(0, 0, 0, 0.38)',
                    '&.MuiInputLabel-shrink': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {warnings.length > 0 && (
                        <InputAdornment position="end">
                          <Tooltip title={warningTitle}>
                            <WarningAmberIcon color="warning" />
                          </Tooltip>
                        </InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={isUIBlocked}
            sx={{ flex: 1, minWidth: 0 }}
          />
          {availableSources.length > 1 && (
            <Button
              title={`Quelle wechseln: Aktuell ${fieldState.selected}.`}
              sx={{
                width: '40px',
                height: '40px',
                minWidth: '40px',
              }}
              onClick={() => handleCycleSource(fieldKey)}
              variant="outlined"
            >
              {getButtonIcon()}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default ResponsibleUserSelector;
